import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { purchaseService } from '../services/purchaseService';
import { productService } from '../services/productService';
import { authService } from '../services/authService';
import type { Product } from '../types/product';

interface SellServiceModalProps {
  businessProfileId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

// "Sprzedaj usługę" — registers a service bought on-site (issue-comp flow).
// Extracted from PurchaseManagement so the Operacje tile can open it directly.
export function SellServiceModal({ businessProfileId, onClose, onSuccess }: SellServiceModalProps) {
  const { t } = useTranslation();

  const [issueCompProducts, setIssueCompProducts] = useState<Product[]>([]);
  const [issueCompProductId, setIssueCompProductId] = useState('');
  const [issueCompEmail, setIssueCompEmail] = useState('');
  const [issueCompName, setIssueCompName] = useState('');
  const [issueCompPhone, setIssueCompPhone] = useState('');
  const [issueCompReason, setIssueCompReason] = useState('');
  const [issueCompSendEmail, setIssueCompSendEmail] = useState(true);
  const [issuingComp, setIssuingComp] = useState(false);
  const [issueCompError, setIssueCompError] = useState<string | null>(null);

  // Load active products once when the modal opens.
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const token = authService.storage.getToken();
        if (!token) return;
        const products = await productService.getProducts(businessProfileId, token);
        setIssueCompProducts(products.filter((p) => p.isActive));
      } catch (err) {
        setIssueCompError(err instanceof Error ? err.message : t('purchases.issueCompError'));
      }
    };
    loadProducts();
  }, [businessProfileId, t]);

  const handleIssueComp = async () => {
    if (!issueCompProductId || !issueCompEmail.trim() || issueCompReason.trim().length < 3) {
      setIssueCompError(t('purchases.issueCompValidation'));
      return;
    }
    setIssuingComp(true);
    setIssueCompError(null);
    try {
      await purchaseService.issueComp(businessProfileId, {
        productId: issueCompProductId,
        customerEmail: issueCompEmail.trim(),
        customerName: issueCompName.trim() || undefined,
        customerPhone: issueCompPhone.trim() || undefined,
        reason: issueCompReason.trim(),
        sendEmail: issueCompSendEmail,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setIssueCompError(err instanceof Error ? err.message : t('purchases.issueCompError'));
    } finally {
      setIssuingComp(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-border-strong">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">{t('purchases.issueCompTitle')}</h3>
            <button
              onClick={() => onClose()}
              className="text-white/70 hover:text-white transition-colors"
              aria-label={t('purchases.cancel')}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-white/60 mt-2">{t('purchases.issueCompHint')}</p>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('purchases.issueCompProduct')}</label>
            <select
              value={issueCompProductId}
              onChange={(e) => setIssueCompProductId(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
            >
              {/* CSS variables on <option> so the native dropdown popup resolves
                  surface + ink against the active theme — not against the OS
                  chrome (which would render light-on-light in dark mode). */}
              <option
                value=""
                disabled
                style={{ color: 'var(--color-ink-muted)', backgroundColor: 'var(--color-surface-card)' }}
              >
                {t('purchases.issueCompProductPlaceholder')}
              </option>
              {issueCompProducts.map((p) => (
                <option
                  key={p.id}
                  value={p.id}
                  style={{ color: 'var(--color-ink-primary)', backgroundColor: 'var(--color-surface-card)' }}
                >
                  {p.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('purchases.issueCompEmail')}</label>
            <input
              type="email"
              value={issueCompEmail}
              onChange={(e) => setIssueCompEmail(e.target.value)}
              placeholder="adres@example.com"
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('purchases.issueCompName')}</label>
              <input
                type="text"
                value={issueCompName}
                onChange={(e) => setIssueCompName(e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('purchases.issueCompPhone')}</label>
              <input
                type="tel"
                value={issueCompPhone}
                onChange={(e) => setIssueCompPhone(e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('purchases.issueCompReason')} <span className="text-brand-warm">*</span>
            </label>
            <textarea
              value={issueCompReason}
              onChange={(e) => setIssueCompReason(e.target.value)}
              rows={2}
              placeholder={t('purchases.issueCompReasonPlaceholder')}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
            />
            <p className="text-xs text-white/60 mt-1">{t('purchases.issueCompReasonHelp')}</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-white cursor-pointer select-none">
            <input
              type="checkbox"
              checked={issueCompSendEmail}
              onChange={(e) => setIssueCompSendEmail(e.target.checked)}
              className="w-4 h-4 accent-brand-primary"
            />
            {t('purchases.issueCompSendEmail')}
          </label>

          {issueCompError && (
            <div className="p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
              <p className="text-sm text-brand-warm">{issueCompError}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border-strong flex justify-end gap-3">
          <button
            onClick={() => onClose()}
            className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            {t('purchases.cancel')}
          </button>
          <button
            onClick={handleIssueComp}
            disabled={issuingComp || !issueCompProductId || !issueCompEmail || issueCompReason.trim().length < 3}
            className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {issuingComp ? t('purchases.issueCompSubmitting') : t('purchases.issueCompSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
